import type { ThemeClasses, Group } from "../types";
import type { Device } from "../../../../../../store/admin/devicesStore";
import type { User } from "../../../../../../store/admin/usersStore";
import Modal from "../../../../../../components/base/Modal";
import { formatDate } from "../utils/helpers";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useTranslation } from "react-i18next";
interface GroupDetailsModalProps {
  isOpen: boolean;
  group: Group | null;
  themeClasses: ThemeClasses;
  isDarkMode?: boolean;
  devices: Device[];
  monitoristas: User[];
  onClose: () => void;
  onEdit: () => void;
}

export function GroupDetailsModal({
  isOpen,
  group,
  themeClasses,
  devices,
  monitoristas,
  onClose,
  onEdit,
}: GroupDetailsModalProps) {
  const { t } = useTranslation("adminSections");
  const { isMonitor } = useIsMonitor();
  const disableCRUD = useDisableCRUD();

  if (!isOpen || !group) return null;

  const groupDevices = devices.filter((d) => group.devices_ids?.includes(d.id));
  const groupMonitoristas = monitoristas.filter((m) => group.monitorista_ids?.includes(m.id));
  return (
    <Modal
      title="Detalles del Grupo"
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full max-w-[550px]"
      showEditButton={true}
      onEdit={onEdit}
      disableCRUD={disableCRUD || isMonitor}
      content={
        <div className="flex flex-col gap-6">
          {/* Info básica */}
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                Nombre
              </label>
              <p className={`text-lg font-semibold ${themeClasses.text}`}>
                {group.name}
              </p>
            </div>

            <div>
              <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                Descripción
              </label>
              <p className={`${themeClasses.text}`}>
                {group.description || "Sin descripción"}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div>
                <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                  Estado
                </label>
                <div className="mt-1">
                  <span
                    className={`py-1 rounded-full text-sm font-medium ${themeClasses.text}`}
                  >
                    {group?.status?.name ? getStatusHumanLabel(group.status.name, t) : t("groups.fields.noStatus")}
                  </span>
                </div>
              </div>

              <div>
                <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                  Última actualización
                </label>
                <p className={`${themeClasses.text}`}>
                  {formatDate(group.updatedAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Dispositivos */}
          <div>
            <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
              Dispositivos ({groupDevices.length})
            </label>
            <div
              className={`mt-2 border ${themeClasses.cardBorder} rounded-lg max-h-60 overflow-y-auto`}
            >
              {groupDevices.length === 0 ? (
                <div
                  className={`p-4 text-center ${themeClasses.textSecondary}`}
                >
                  No hay dispositivos asignados
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {groupDevices.map((device) => (
                    <div
                      key={device.id}
                      className={`p-3 flex items-center justify-between ${themeClasses.cardBg}`}
                    >
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>
                          {device.identifier || device.vehicleName || device.id}
                        </p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>
                          {device.vehicle?.name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Monitoristas */}
          <div>
            <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
              Monitoristas ({groupMonitoristas.length})
            </label>
            <div
              className={`mt-2 border ${themeClasses.cardBorder} rounded-lg max-h-60 overflow-y-auto`}
            >
              {groupMonitoristas.length === 0 ? (
                <div
                  className={`p-4 text-center ${themeClasses.textSecondary}`}
                >
                  No hay monitoristas asignados
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {groupMonitoristas.map((monitorista) => (
                    <div
                      key={monitorista.id}
                      className={`p-3 flex items-center justify-between ${themeClasses.cardBg}`}
                    >
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>
                          {monitorista.first_name} {monitorista.last_name}
                        </p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>
                          {monitorista.email}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
}
